import { HelmetProvider } from 'react-helmet-async';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ProtectedRoute from './components/common/ProtectedRoute';
import ScrollToTop from './components/common/ScrollToTop';
import CartBar from './components/cart/CartBar';
import Home from './pages/Home';
import Products from './pages/Products';
import Campaign from './pages/Campaign';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Register from './pages/Register';
import Search from './pages/Search';
import Support from './pages/Support';
import Careers from './pages/Careers';
import NotFound from './pages/NotFound';
import KhaltiCallback from './pages/KhaltiCallback';
import EsewaCallback from './pages/EsewaCallback';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Loading from './components/common/Loading';
import './App.css';

// While the shop takes orders on WhatsApp there is no cart to open, so a
// bookmarked cart or checkout link lands on the home page instead of a dead
// cart. The mode is a setting, so the decision waits for it to load.
const CartRoute = ({ children }) => {
  const { loading, whatsappOrdering } = useSettings();

  if (loading) {
    return <Loading />;
  }

  if (whatsappOrdering) {
    return <Navigate to="/" replace />;
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
};

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <SettingsProvider>
          <AuthProvider>
            <CartProvider>
              <Router>
                <div className="App">
                  <ScrollToTop />
                  <Header />
                  <main className="main-content">
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/search" element={<Search />} />
                      <Route path="/products" element={<Products />} />
                      <Route
                        path="/products/:slug"
                        element={<ProductDetail />}
                      />
                      <Route path="/campaigns/:slug" element={<Campaign />} />
                      <Route path="/support" element={<Support />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route
                        path="/privacy-policy"
                        element={<PrivacyPolicy />}
                      />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route
                        path="/cart"
                        element={
                          <CartRoute>
                            <Cart />
                          </CartRoute>
                        }
                      />
                      <Route
                        path="/checkout"
                        element={
                          <CartRoute>
                            <Checkout />
                          </CartRoute>
                        }
                      />
                      <Route
                        path="/payment/khalti/callback"
                        element={
                          <ProtectedRoute>
                            <KhaltiCallback />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/payment/esewa/callback"
                        element={
                          <ProtectedRoute>
                            <EsewaCallback />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  <CartBar />
                  <Footer />
                </div>
              </Router>
            </CartProvider>
          </AuthProvider>
        </SettingsProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
